import { gql } from '@apollo/client';
import {
  AlertTitle,
  Box,
  Button,
  Heading,
  HStack,
  IconButton,
  Text,
  Tooltip,
  useDisclosure,
  VStack,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import {
  ScheduleCard_CustomerFragment,
  ScheduleCard_DisplayFragment,
  useDisplayClearScheduleMutation,
  useDisplaySyncScheduleMutation,
} from '~components/displays/DisplayDetail/summary/__generated__/ScheduleCard.graphql';
import { ScheduleTimeline } from '~components/displays/DisplayDetail/summary/ScheduleTimeline';
import { SummaryCard } from '~components/displays/DisplayDetail/summary/SummaryCard';
import { ManageScheduleModal } from '~components/displays/ManageScheduleModal';
import { WarningAlert } from '~components/ui/Alert';
import { FormLabelPendingIndicator } from '~components/ui/FormLabelPendingIndicator';
import { TrashIcon } from '~components/ui/icons';

interface Props {
  display: ScheduleCard_DisplayFragment;
  customer: ScheduleCard_CustomerFragment;
  isDisabled?: boolean;
}

/**
 * Counterpart of `PowerScheduleCard`: same summary card, but selecting and changing
 * the display's `schedule` instead of its power schedule.
 */
export function ScheduleCard({ display, customer, isDisabled = false }: Props) {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { t } = useTranslation();
  const [clearSchedule, { loading: isDeleting }] = useDisplayClearScheduleMutation();
  const [syncSchedule, { loading: isSyncingSchedule }] = useDisplaySyncScheduleMutation();

  const schedule = display.schedule?.schedule;
  const isSyncing = display.schedule?.latestJob?.__typename === 'ScheduleUpdateInProgress';
  const isOutOfSync = !isSyncing && display.schedule?.isSynced === false;

  const handleDeleteSchedule = () => {
    clearSchedule({ variables: { displayId: display.id } });
  };

  const handleSyncSchedule = () => {
    if (schedule?.id) {
      syncSchedule({ variables: { displayId: display.id, scheduleId: schedule.id } });
    }
  };

  return (
    <SummaryCard title={t('schedule')}>
      <VStack spacing="5" alignItems="stretch">
        {isSyncing && (
          <WarningAlert>
            <AlertTitle>{t('scheduleSyncing')}</AlertTitle>
          </WarningAlert>
        )}
        {isOutOfSync && (
          <WarningAlert
            actionButton={
              <Button
                variant="outline"
                size="sm"
                colorScheme="orange"
                color="orange.700"
                onClick={handleSyncSchedule}
                isLoading={isSyncingSchedule}
                isDisabled={isDisabled || isSyncing}
              >
                {t('sync')}
              </Button>
            }
          >
            <AlertTitle>{t('scheduleOutOfSync')}</AlertTitle>
          </WarningAlert>
        )}
        <HStack justifyContent="space-between" alignContent="start">
          <HStack gap="24px" flex="1" minW="0">
            <Box display="flex" flexDir="column" minW="0">
              <HStack spacing="1" minW="0">
                <Tooltip
                  bg="gray.700"
                  color="white"
                  fontSize="sm"
                  borderRadius="md"
                  placement="bottom-start"
                  label={schedule?.title}
                  isDisabled={!schedule?.title}
                >
                  <Heading size="md" color="gray.900" mb="0" lineHeight="7" isTruncated>
                    {schedule?.title || t('noSchedule')}
                  </Heading>
                </Tooltip>
                <FormLabelPendingIndicator isPending={isSyncing} label={t('syncingSchedule')} />
              </HStack>
              {!schedule && <Text color="gray.500">{t('noScheduleSync')}</Text>}
            </Box>
            {schedule && (
              <IconButton
                icon={<TrashIcon height="24px" width="24px" />}
                aria-label={t('delete')}
                size="lg"
                variant="ghost"
                colorScheme="gray"
                onClick={handleDeleteSchedule}
                isDisabled={isDisabled || isSyncing || isDeleting}
                isLoading={isDeleting}
              />
            )}
          </HStack>
          <Button
            variant="outline"
            colorScheme="blue"
            size="sm"
            onClick={onOpen}
            isDisabled={isDisabled || isSyncing}
          >
            {schedule ? t('changeSchedule') : t('chooseSchedule')}
          </Button>
        </HStack>
        <ScheduleTimeline display={display} />
      </VStack>

      <ManageScheduleModal
        customer={customer}
        display={display}
        isOpen={isOpen}
        onCancel={onClose}
        onSuccess={onClose}
      />
    </SummaryCard>
  );
}

ScheduleCard.graphql = {
  fragments: {
    ScheduleCard_customer: gql`
      fragment ScheduleCard_customer on Customer {
        id
        ...ManageScheduleModal_customer
      }
    `,
    ScheduleCard_display: gql`
      fragment ScheduleCard_display on Display {
        id
        ...ManageScheduleModal_display
        ...ScheduleTimeline_display
        schedule {
          isSynced
          latestJob {
            __typename
          }
          schedule {
            title
          }
        }
      }
    `,
  },
  mutations: {
    DisplayClearSchedule: gql`
      mutation DisplayClearSchedule($displayId: ID!) {
        displayBulkUpdateSchedule(input: { displayIds: [$displayId], scheduleId: null }) {
          displays {
            id
            ...ScheduleCard_display
          }
        }
      }
    `,
    DisplaySyncSchedule: gql`
      mutation DisplaySyncSchedule($displayId: ID!, $scheduleId: ID!) {
        displayBulkUpdateSchedule(input: { displayIds: [$displayId], scheduleId: $scheduleId }) {
          displays {
            id
            ...ScheduleCard_display
          }
        }
      }
    `,
  },
};
